import { isEmpty, size } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useInventoryEventBulkActionsState } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/useInventoryEventBulkActionsState';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useContentContext } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { getListingsSectionalDataForEvents } from 'src/navigations/Routes/Inventory';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import { newGuidId } from 'src/utils/idUtils';
import {
  Event,
  Feature,
  Listing,
  ListingDetailDataField,
  ListingGroupItemInput,
  ListingQuery,
  MergeListingGroupInput,
} from 'src/WebApiController';

import { GroupListingSortingCriteria } from '../GroupListings/components/groupingTypes';
import { flattenListingGroup } from '../GroupListings/components/groupingUtils';
import { groupPrioritySort } from '../GroupListings/components/ListingGroupInput.hook';
import {
  GroupListingsFormProps,
  GroupListingsState,
  GroupSortByType,
  ListingGroupType,
} from './GroupListingsV2.types';
import { ListingGroupsGenerator } from './ZoneMapGroupsGenerator';

const stringSortWrapper = (
  a: string | null | undefined,
  b: string | null | undefined
) => {
  const parsedA = parseFloat(a ?? '');
  const parsedB = parseFloat(b ?? '');
  if (isNaN(parsedA) || isNaN(parsedB)) {
    return (a ?? '').localeCompare(b ?? '');
  }
  return parsedA - parsedB;
};

const sortGroupListings = <T extends { listingId: number }>(
  groupListings: T[],
  eventListings: Listing[],
  groupSort?: GroupSortByType | null
) => {
  if (!groupSort) {
    return groupListings;
  }
  const groupListingIds = groupListings.map((l) => l.listingId);

  const isAssending = ![
    GroupSortByType.RowDesc,
    GroupSortByType.SectionDesc,
    GroupSortByType.UnsoldQuantityDesc,
  ].includes(groupSort);
  const orderFactor = isAssending ? 1 : -1;
  const sortedListingIds = eventListings
    .filter((l) => groupListingIds.includes(l.id))
    .toSorted((a, b) => {
      if (
        [GroupSortByType.SectionAsc, GroupSortByType.SectionDesc].includes(
          groupSort
        )
      ) {
        return (
          orderFactor *
          stringSortWrapper(a.seating?.section, b.seating?.section)
        );
      }
      if (
        [GroupSortByType.RowAsc, GroupSortByType.RowDesc].includes(groupSort)
      ) {
        return orderFactor * stringSortWrapper(a.seating?.row, b.seating?.row);
      }

      return orderFactor * (a.availQty - b.availQty);
    })
    .map((l) => l.id);

  return groupListings
    .toSorted((a, b) => {
      return (
        sortedListingIds.indexOf(a.listingId) -
        sortedListingIds.indexOf(b.listingId)
      );
    })
    .map((l, index) => ({ ...l, priority: index + 1 }));
};

export const useSetListingGroupValue = (listingGroupId: string) => {
  const { watch, setValue } = useFormContext<GroupListingsFormProps>();
  const listingGroups = watch('mergeListingGroupInputs');

  const setListingGroupValue = useCallback(
    (value: Partial<MergeListingGroupInput>) => {
      setValue(
        'mergeListingGroupInputs',
        listingGroups.map((group) => {
          if (group.listingGroupId === listingGroupId) {
            return {
              ...group,
              ...value,
            };
          }
          return group;
        })
      );
    },
    [listingGroupId, listingGroups, setValue]
  );

  return setListingGroupValue;
};

const useLoadListingGroups = (input: GroupListingsFormProps) => {
  const { venueMapInfo, venueZoneConfigs } = useEventMapContext();

  const contentContext = useContentContext();

  const getIdToSortingCriteria = useCallback((listings: Listing[]) => {
    return (
      (listings.flatMap(flattenListingGroup) as Listing[]).reduce(
        (acc, { id, isAdminHold, availQty }) => {
          acc[id] = {
            isAdminHold,
            availableQuantity: availQty,
          };
          return acc;
        },
        {} as Record<number, GroupListingSortingCriteria>
      ) ?? {}
    );
  }, []);

  const loadFromZoneMap = useCallback(
    (
      event: Event | undefined,
      listings: Listing[]
    ): MergeListingGroupInput[] => {
      if (
        event == null ||
        isEmpty(venueZoneConfigs?.sectionSectionGroupLookup)
      ) {
        return [];
      }
      const idToSortingCriteria = getIdToSortingCriteria(listings ?? []);

      const rowRanges = input.groupBys[0].ranges ?? [];

      let generator = new ListingGroupsGenerator(
        listings,
        venueMapInfo?.sections ?? [],
        Object.values(venueZoneConfigs?.sectionSectionGroupLookup ?? {})
      )
        .generateBaseZoneGroups()
        .splitByRanges(rowRanges, ListingGroupType.Row, contentContext);

      if (input.groupBys.length > 1) {
        const secondaryGroup = input.groupBys[1];
        if (isEmpty(secondaryGroup.ranges)) {
          generator = generator.splitWithGroupBy(
            secondaryGroup,
            contentContext
          );
        } else {
          generator = generator.splitByRanges(
            secondaryGroup.ranges ?? [],
            secondaryGroup.groupingType,
            contentContext
          );
        }
      }

      return generator
        .getListingGroups()
        .map((group): MergeListingGroupInput => {
          const listingGroupId = newGuidId();
          const { undAbsAmt, undRelAmt } = input.groupUndercutSettings ?? {};

          const items = sortGroupListings(
            group.listings.map((l, i) => ({
              ...l,
              listingId: l.id,
              priority: i + 1,
            })),
            listings,
            input.groupSortBy
          )
            .toSorted(
              groupPrioritySort(
                idToSortingCriteria,
                input.deprioritizedQuantities
              )
            )
            .map((l, i): ListingGroupItemInput => {
              const undercutSetting = {
                undAbsAmt: undAbsAmt ? undAbsAmt * i : null,
                undRelAmt: undRelAmt ? Math.min(undRelAmt * i, 1) : null,
                actRankUndAbsAmt: null,
                actRankUndRelAmt: null,
              };
              return {
                listingId: l.listingId,
                priority: i + 1,
                undercutSetting,
                groupId: l.ltGrpId,
              };
            });
          return {
            listingGroupId,
            name: group.name,
            minActiveListings: null,
            maxActiveListings: null,
            targetPercentage: null,
            viagogoEventId: event.viagId,
            viagogoMappingId: event.mpId,
            desiredActiveListings: input.desiredActiveListings,
            deprioritizedQuantities: input.deprioritizedQuantities,
            marketplaceSettings: input.marketplaceSettings,
            groupUndercutSetting: input.groupUndercutSettings,
            listingGroupItems: items,
            groupingMethods: null,
            inheritListingGroupSettings: false,
            maintainPriceStagger: false,
          };
        });
    },
    [
      venueZoneConfigs?.sectionSectionGroupLookup,
      getIdToSortingCriteria,
      input.groupBys,
      input.groupUndercutSettings,
      input.groupSortBy,
      input.deprioritizedQuantities,
      input.desiredActiveListings,
      input.marketplaceSettings,
      venueMapInfo?.sections,
      contentContext,
    ]
  );

  const loadFromGroupBys = useCallback(
    (event: Event | undefined, listings: Listing[]) => {
      if (event == null) {
        return [];
      }
      let generator = new ListingGroupsGenerator(
        listings,
        venueMapInfo?.sections ?? [],
        Object.values(venueZoneConfigs?.sectionSectionGroupLookup ?? {})
      );
      const idToSortingCriteria = getIdToSortingCriteria(listings);

      for (const groupBy of input.groupBys) {
        if (isEmpty(groupBy.ranges)) {
          generator = generator.splitWithGroupBy(groupBy, contentContext);
        } else {
          generator = generator.splitByRanges(
            groupBy.ranges ?? [],
            groupBy.groupingType,
            contentContext
          );
        }
      }

      const { undAbsAmt, undRelAmt } = input.groupUndercutSettings ?? {};

      return generator
        .getListingGroups()
        .map((group): MergeListingGroupInput => {
          const listingGroupId = newGuidId();
          const sortedItems = sortGroupListings(
            group.listings.map((l, i) => ({
              ...l,
              listingId: l.id,
              priority: i + 1,
            })),
            listings,
            input.groupSortBy
          )
            .toSorted(
              groupPrioritySort(
                idToSortingCriteria,
                input.deprioritizedQuantities
              )
            )
            .map((l, i): ListingGroupItemInput => {
              const undercutSetting = {
                undAbsAmt: undAbsAmt ? undAbsAmt * i : null,
                undRelAmt: undRelAmt ? Math.min(undRelAmt * i, 1) : null,
                actRankUndAbsAmt: null,
                actRankUndRelAmt: null,
              };
              return {
                listingId: l.listingId,
                priority: i + 1,
                undercutSetting,
                groupId: l.ltGrpId,
              };
            });
          return {
            listingGroupId,
            name: group.name,
            minActiveListings: null,
            maxActiveListings: null,
            targetPercentage: null,
            viagogoEventId: event.viagId,
            viagogoMappingId: event.mpId,
            desiredActiveListings: input.desiredActiveListings,
            deprioritizedQuantities: input.deprioritizedQuantities,
            marketplaceSettings: input.marketplaceSettings,
            groupUndercutSetting: input.groupUndercutSettings,
            listingGroupItems: sortedItems,
            groupingMethods: null,
            inheritListingGroupSettings: false,
            maintainPriceStagger: false,
          };
        });
    },
    [
      contentContext,
      getIdToSortingCriteria,
      input.deprioritizedQuantities,
      input.desiredActiveListings,
      input.groupBys,
      input.groupSortBy,
      input.groupUndercutSettings,
      input.marketplaceSettings,
      venueMapInfo?.sections,
      venueZoneConfigs?.sectionSectionGroupLookup,
    ]
  );

  return {
    loadFromGroupBys,
    loadFromZoneMap,
  };
};

export const useEventListings = (event: Event) => {
  const { eventsTransformed } = useCatalogDataContext();

  const eventDate = useMemo(() => {
    return (
      eventsTransformed?.find((e) => e.event.virtId === event?.virtId) ?? null
    );
  }, [event, eventsTransformed]);

  const { filterQueryWithEventIds } = useInventoryEventBulkActionsState(event);

  const eventListings = useMemo(() => {
    const selectedListingIds = filterQueryWithEventIds.entityIds ?? [];
    if (
      selectedListingIds.length === 0 ||
      eventDate?.entities?.listings == null
    ) {
      return (
        (eventDate?.entities?.listings?.flatMap(
          flattenListingGroup
        ) as Listing[]) ?? []
      );
    }

    return (
      (eventDate?.entities?.listings
        .filter((l) => selectedListingIds.includes(l.id))
        .flatMap(flattenListingGroup) as Listing[]) ?? []
    );
  }, [filterQueryWithEventIds.entityIds, eventDate?.entities?.listings]);

  return eventListings;
};

export const useGroupListingsAction = (
  event: Event,
  onSubmit: (input: GroupListingsFormProps) => void,
  selectMultiEvents?: Event[],
  onMultiEventSubmitStarted?: () => void
) => {
  const [state, setState] = useState<GroupListingsState>(
    GroupListingsState.Configure
  );
  const hasGroupListingsAcrossEventsFeature = useUserHasFeature(
    Feature.GroupListingsAcrossEvents
  );
  const eligibleForMultiEventGrouping = useMemo(() => {
    if (!hasGroupListingsAcrossEventsFeature) {
      return false;
    }
    return selectMultiEvents != null && selectMultiEvents.length > 0;
  }, [hasGroupListingsAcrossEventsFeature, selectMultiEvents]);

  const eventListings = useEventListings(event);
  const { watch, setValue, handleSubmit } =
    useFormContext<GroupListingsFormProps>();

  const input = watch();

  const primaryGroupType = useMemo(() => {
    if (size(input.groupBys) < 1) {
      return null;
    }
    return input.groupBys[0].groupingType;
  }, [input.groupBys]);

  const { loadFromGroupBys, loadFromZoneMap } = useLoadListingGroups(input);

  const { activeAccountWebClientConfig } = useAppContext();
  const { affectedVisibleListingIds, filterQueryWithEventIds } =
    useInventoryEventBulkActionsState();
  const { filterQuery } = useFilterQueryContext<ListingQuery>();

  const onSubmitMultipleEvents = useCallback(
    async (inputFormData: GroupListingsFormProps) => {
      if (!eligibleForMultiEventGrouping || !selectMultiEvents) {
        return;
      }
      onMultiEventSubmitStarted?.();
      const expandedEventData = await getListingsSectionalDataForEvents(
        selectMultiEvents.map((e) => e.virtId) ?? [],
        filterQuery ?? EmptyListingQuery,
        [ListingDetailDataField.Basic],
        { activeAccountWebClientConfig }
      );

      const groups: MergeListingGroupInput[] = [];
      for (const [evtId, evtData] of Object.entries(expandedEventData)) {
        if (evtData.listings == null) {
          continue;
        }
        const flattenedListings = evtData.listings.flatMap(
          flattenListingGroup
        ) as Listing[];
        const affectedListings = flattenedListings.filter((l) =>
          affectedVisibleListingIds.includes(l.id)
        );
        const eligibleListings = (
          filterQueryWithEventIds.eventOrMappingIds ?? []
        ).includes(evtId)
          ? flattenedListings
          : affectedListings;
        if (eligibleListings.length === 0) {
          continue;
        }
        const eventGroups =
          primaryGroupType === ListingGroupType.ZoneMap
            ? loadFromZoneMap(event, eligibleListings)
            : loadFromGroupBys(event, eligibleListings);
        groups.push(...eventGroups);
      }
      const listingInput = {
        ...inputFormData,
        mergeListingGroupInputs: groups,
      };
      onSubmit(listingInput);
    },
    [
      eligibleForMultiEventGrouping,
      selectMultiEvents,
      onMultiEventSubmitStarted,
      filterQuery,
      activeAccountWebClientConfig,
      onSubmit,
      filterQueryWithEventIds.eventOrMappingIds,
      primaryGroupType,
      loadFromZoneMap,
      event,
      loadFromGroupBys,
      affectedVisibleListingIds,
    ]
  );

  const onFooterNav = useCallback(
    (back = false) => {
      // Move from configure to preview
      if (state === GroupListingsState.Configure) {
        if (eligibleForMultiEventGrouping) {
          handleSubmit(onSubmitMultipleEvents)();
          return;
        }
        if (primaryGroupType === ListingGroupType.ZoneMap) {
          const groups = loadFromZoneMap(event, eventListings);
          setValue('mergeListingGroupInputs', groups);
        } else {
          const groups = loadFromGroupBys(event, eventListings);
          setValue('mergeListingGroupInputs', groups);
        }
        setState(GroupListingsState.Review);
      }

      // Submit
      if (state === GroupListingsState.Review) {
        if (back) {
          setState(GroupListingsState.Configure);
        } else {
          handleSubmit(onSubmit ?? (() => {}))();
        }
      }
    },
    [
      state,
      eligibleForMultiEventGrouping,
      primaryGroupType,
      handleSubmit,
      onSubmitMultipleEvents,
      loadFromZoneMap,
      event,
      eventListings,
      setValue,
      loadFromGroupBys,
      onSubmit,
    ]
  );

  return {
    state,
    onFooterNav,
    eventListings,
  };
};
